import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import './App.css';

import OrganizationChart, { OrganizationChartRef } from './components/OrganizationChart';
import NavigationBar from './components/NavigationBar';
import NotificationSystem, { useNotifications } from './components/NotificationSystem';
import EntityTable from './components/EntityTable';
import SettingsPanel from './components/SettingsPanel';
import { downloadExcel } from './utils/excelExporter';
import {
  CompanyNode,
  ValidationResult,
  LayoutDirection,
  SearchResult,
  NodeCustomization
} from './types';
import { saveProjectToFile, loadProjectFromFile, saveToLocalStorage, loadFromLocalStorage, clearLocalStorage, SavedProject } from './utils/projectSaver';
import { HistoryManager, HistoryState } from './utils/undoRedo';
import { AppSettings, loadSettings, saveSettings } from './utils/settings';
import { ExcelProcessor } from './utils/excelProcessor';
import { SampleDataGenerator } from './utils/sampleDataGenerator';

function App() {
  // Ref for OrganizationChart
  const chartRef = useRef<OrganizationChartRef>(null);
  const projectLoadInputRef = useRef<HTMLInputElement>(null);
  const historyManagerRef = useRef<HistoryManager>(new HistoryManager());
  
  // State management
  const [companyNodes, setCompanyNodes] = useState<CompanyNode[]>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const validationResultRef = useRef<ValidationResult | null>(null);
  
  // Notification system
  const { notifications, addNotification, removeNotification } = useNotifications();
  
  // Chart configuration
  const [settings, setSettings] = useState<AppSettings>(() => loadSettings());
  useEffect(() => { saveSettings(settings); }, [settings]);

  const [showSettingsPanel, setShowSettingsPanel] = useState(() => {
    return localStorage.getItem('hvg-settings-panel-open') === 'true';
  });
  useEffect(() => {
    localStorage.setItem('hvg-settings-panel-open', String(showSettingsPanel));
  }, [showSettingsPanel]);

  // Aliases derived from settings (single source of truth)
  const layout = settings.layoutDirection;
  const showCountryFlags = settings.showCountryFlags;
  const percentageDisplayMode = settings.percentageDisplayMode;

  const [maxDepth, setMaxDepth] = useState<number | 'all'>('all');
  const [showEntityTable, setShowEntityTable] = useState(() => {
    return localStorage.getItem('hvg-entity-table-open') === 'true';
  });
  useEffect(() => {
    localStorage.setItem('hvg-entity-table-open', String(showEntityTable));
  }, [showEntityTable]);
  // Cluster-grouping nu correct via compound nodes (v1.5.0+)
  const showClusterGrouping = settings.clusterGrouping;
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Easter egg: Show sample data button after 20 logo clicks
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [showSampleButton, setShowSampleButton] = useState(false);
  
  // Auto-save settings
  const autoSaveEnabled = true; // Could make this a setting
  
  // Undo/Redo state
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  
  // Helper function to create a history snapshot
  const createHistorySnapshot = useCallback((): HistoryState => {
    const positions = chartRef.current?.getNodePositions() || {};
    return {
      companyNodes,
      nodePositions: positions,
      layout,
      maxDepth,
      showCountryFlags,
      percentageDisplayMode,
    };
  }, [companyNodes, layout, maxDepth, showCountryFlags, percentageDisplayMode]);
  
  // Helper function to push current state to history
  const pushToHistory = useCallback((skipSnapshot: boolean = false) => {
    if (!skipSnapshot) {
      const snapshot = createHistorySnapshot();
      historyManagerRef.current.push(snapshot);
      setCanUndo(historyManagerRef.current.canUndo());
      setCanRedo(historyManagerRef.current.canRedo());
    }
  }, [createHistorySnapshot]);
  
  // Restore state from history snapshot
  const restoreFromHistory = useCallback((state: HistoryState) => {
    setCompanyNodes(state.companyNodes);
    setMaxDepth(state.maxDepth);
    setSettings(prev => ({
      ...prev,
      layoutDirection: state.layout,
      showCountryFlags: state.showCountryFlags,
      ...(state.percentageDisplayMode ? { percentageDisplayMode: state.percentageDisplayMode } : {}),
    }));
    
    // Restore node positions after a brief delay
    setTimeout(() => {
      if (chartRef.current && state.nodePositions) {
        chartRef.current.setNodePositions(state.nodePositions);
      }
    }, 100);
  }, []);

  // Filter nodes by depth
  const filteredNodes = useMemo(() => {
    if (maxDepth === 'all') {
      return companyNodes;
    }
    return companyNodes.filter(node => node.depth < maxDepth);
  }, [companyNodes, maxDepth]);

  // Search functionality
  const searchResults: SearchResult[] = useMemo(() => {
    if (!searchTerm.trim() || companyNodes.length === 0) {
      return [];
    }
    
    return companyNodes
      .filter(node => 
        node.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .slice(0, 5)
      .map(node => ({
        id: node.id,
        name: node.name,
        country: node.country,
        isVisible: filteredNodes.some(fn => fn.id === node.id)
      }));
  }, [searchTerm, companyNodes, filteredNodes]);

  // Event handlers
  const handleDataLoaded = useCallback((nodes: CompanyNode[]) => {
    setCompanyNodes(nodes);
    setSelectedNodeId(null);
    setSelectedNodeIds([]);
    // Clear auto-save when loading new data to avoid confusion
    clearLocalStorage();
    // Initialize history with the loaded data
    setTimeout(() => {
      const snapshot = {
        companyNodes: nodes,
        nodePositions: chartRef.current?.getNodePositions() || {},
        layout,
        maxDepth,
        showCountryFlags,
        percentageDisplayMode,
      };
      historyManagerRef.current.initialize(snapshot);
      setCanUndo(false);
      setCanRedo(false);
    }, 500);
    addNotification('success', 'Gegevens geladen', `Succesvol ${nodes.length} entiteiten geladen`);
  }, [addNotification, layout, maxDepth, showCountryFlags, percentageDisplayMode]);

  const handleError = useCallback((errorMessage: string) => {
    addNotification('error', 'Fout', errorMessage);
    validationResultRef.current = null;
  }, [addNotification]);

  const handleValidationResult = useCallback((result: ValidationResult) => {
    validationResultRef.current = result;
    if (!result.isValid) {
      const errorCount = result.errors.length;
      const warningCount = result.warnings.length;
      addNotification(
        'error', 
        'Validatie mislukt', 
        `${errorCount} fout${errorCount !== 1 ? 'en' : ''} gevonden${warningCount > 0 ? ` en ${warningCount} waarschuwing${warningCount !== 1 ? 'en' : ''}` : ''}`
      );
    } else if (result.warnings.length > 0) {
      addNotification(
        'warning', 
        'Validatiewaarschuwingen', 
        `${result.warnings.length} waarschuwing${result.warnings.length !== 1 ? 'en' : ''} gevonden - gegevens succesvol geladen`
      );
    }
  }, [addNotification]);

  const handleNodeClick = useCallback((nodeId: string) => {
    setSelectedNodeId(prev => prev === nodeId ? null : nodeId);
  }, []);

  // Handle ReactFlow selection change (box-select and Shift+click)
  const handleSelectionChange = useCallback(({ nodes }: { nodes: Array<{ id: string }> }) => {
    const ids = nodes.map(n => n.id);
    setSelectedNodeIds(ids);
    // If exactly one node is selected, also set it as the primary selected node
    if (ids.length === 1) {
      setSelectedNodeId(ids[0]);
    } else if (ids.length > 1) {
      // Keep the first selected as primary for ownership path highlighting
      setSelectedNodeId(ids[0]);
    }
  }, []);

  // Bulk hide selected nodes
  const handleBulkHide = useCallback(() => {
    if (selectedNodeIds.length === 0) return;
    pushToHistory();
    setCompanyNodes(prev => prev.map(node =>
      selectedNodeIds.includes(node.id)
        ? { ...node, isHidden: true }
        : node
    ));
    addNotification('success', 'Entiteiten verborgen', `${selectedNodeIds.length} entiteiten verborgen`);
    setSelectedNodeIds([]);
    setSelectedNodeId(null);
  }, [selectedNodeIds, pushToHistory, addNotification]);

  // Bulk color change for selected nodes
  const handleBulkColor = useCallback((color: string | undefined) => {
    if (selectedNodeIds.length === 0) return;
    pushToHistory();
    setCompanyNodes(prev => prev.map(node =>
      selectedNodeIds.includes(node.id)
        ? { ...node, customization: { ...node.customization, color } }
        : node
    ));
    addNotification('success', 'Kleur gewijzigd', `Kleur gewijzigd voor ${selectedNodeIds.length} entiteiten`);
  }, [selectedNodeIds, pushToHistory, addNotification]);

  const handleToggleExpand = useCallback((nodeId: string) => {
    pushToHistory();
    setCompanyNodes(prev => prev.map(node => 
      node.id === nodeId 
        ? { ...node, isExpanded: !node.isExpanded }
        : node
    ));
  }, [pushToHistory]);

  const handleToggleHide = useCallback((nodeId: string) => {
    pushToHistory();
    setCompanyNodes(prev => prev.map(node => 
      node.id === nodeId 
        ? { ...node, isHidden: !node.isHidden }
        : node
    ));
    addNotification('success', 'entiteiten verborgen', 'entiteit en dochters zijn verborgen');
  }, [addNotification, pushToHistory]);

  // Clicking on empty canvas deselects all nodes
  const handlePaneClick = useCallback(() => {
    setSelectedNodeId(null);
    setSelectedNodeIds([]);
  }, []);

  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
    setShowSuggestions(term.length >= 2);
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearchTerm('');
    setShowSuggestions(false);
    setSelectedNodeId(null);
    setSelectedNodeIds([]);
  }, []);

  const handleSuggestionSelect = useCallback((nodeId: string) => {
    setSelectedNodeId(nodeId);
    setShowSuggestions(false);

    // If node is not visible at current depth, show all levels
    const isVisible = filteredNodes.some(node => node.id === nodeId);
    if (!isVisible) {
      setMaxDepth('all');
    }

    // Auto-navigate to the selected node
    setTimeout(() => {
      if (chartRef.current) {
        chartRef.current.focusNode(nodeId);
      }
    }, isVisible ? 100 : 500); // Longer delay if depth changed (needs layout recalc)
  }, [filteredNodes]);

  // Chart control handlers
  const handleFitView = useCallback(() => {
    if (chartRef.current) {
      chartRef.current.fitView();
    }
  }, []);

  const handleExportPNG = useCallback(async (fullChart: boolean = false) => {
    if (chartRef.current) {
      try {
        await chartRef.current.exportToPNG(fullChart);
        addNotification('success', 'PNG geëxporteerd', `Organogram succesvol geëxporteerd als PNG (${fullChart ? 'Alles' : 'Venster'})`);
      } catch (error) {
        addNotification('error', 'Export mislukt', 'Kon organogram niet exporteren als PNG');
      }
    }
  }, [addNotification]);

  const handleExportSVG = useCallback(async (fullChart: boolean = false) => {
    if (chartRef.current) {
      try {
        await chartRef.current.exportToSVG(fullChart);
        addNotification('success', 'SVG geëxporteerd', `Organogram succesvol geëxporteerd als SVG (${fullChart ? 'Alles' : 'Venster'})`);
      } catch (error) {
        addNotification('error', 'Export mislukt', 'Kon organogram niet exporteren als SVG');
      }
    }
  }, [addNotification]);

  const handleReset = useCallback(async () => {
    // Show confirmation dialog in Dutch using Electron API (for app icon)
    let confirmed = false;
    
    if (window.electronAPI && window.electronAPI.showConfirmDialog) {
      // Use Electron dialog API (shows app icon)
      confirmed = await window.electronAPI.showConfirmDialog({
        title: 'Weergave Resetten',
        message: 'Weet u zeker dat u de weergave wilt resetten naar de originele staat?',
        detail: 'Dit zal alle aanpassingen ongedaan maken:\n' +
                '• Node posities worden gereset\n' +
                '• Kleuren, vormen en borders worden verwijderd\n' +
                '• Alle aanpassingen gaan verloren\n\n' +
                'U kunt de Wijziging ongedaan maken knop gebruiken om dit ongedaan te maken.'
      });
    } else {
      // Fallback to browser confirm (for web version)
      confirmed = window.confirm(
        'Weet u zeker dat u de weergave wilt resetten naar de originele staat?\n\n' +
        'Dit zal alle aanpassingen ongedaan maken:\n' +
        '- Entiteit posities worden gereset\n' +
        '- Kleuren en vormen worden verwijderd\n' +
        '- Alle aanpassingen gaan verloren\n\n' +
        'U kunt de Undo (CTRL+Z) knop gebruiken om dit ongedaan te maken.'
      );
    }
    
    if (!confirmed) {
      return;
    }
    
    // Save to history before resetting
    pushToHistory();
    
    // Reset node positions
    setSelectedNodeId(null);
    setSelectedNodeIds([]);
    if (chartRef.current) {
      chartRef.current.resetLayout();
    }
    
    // Clear all node customizations (colors, shapes, borders, opacity)
    setCompanyNodes(prev => prev.map(node => ({
      ...node,
      customization: undefined
    })));
    
    addNotification('success', 'Weergave Gereset', 'Chart layout en kaart aanpassingen zijn gereset naar originele staat');
  }, [addNotification, pushToHistory]);

  const handleExpandAll = useCallback(() => {
    pushToHistory();
    setCompanyNodes(prev => prev.map(node => ({ ...node, isExpanded: true })));
    addNotification('success', 'Alles uitgeklapt', 'Alle entiteiten zijn nu uitgeklapt');
  }, [addNotification, pushToHistory]);

  const handleCollapseAll = useCallback(() => {
    pushToHistory();
    setCompanyNodes(prev => prev.map(node => ({ ...node, isExpanded: false })));
    addNotification('success', 'Alles ingeklapt', 'Alle entiteiten zijn nu ingeklapt');
  }, [addNotification, pushToHistory]);

  const handleShowAll = useCallback(() => {
    pushToHistory();
    setCompanyNodes(prev => prev.map(node => ({ ...node, isHidden: false })));
    addNotification('success', 'Alles tonen', 'Alle verborgen entiteiten zijn nu zichtbaar');
  }, [addNotification, pushToHistory]);

  const handleCustomizeNode = useCallback((nodeId: string, customization: NodeCustomization) => {
    pushToHistory();
    setCompanyNodes(prev => prev.map(node => 
      node.id === nodeId 
        ? { ...node, customization: { ...node.customization, ...customization } }
        : node
    ));
    addNotification('success', 'Kaart aangepast', 'Kaartweergave is bijgewerkt');
  }, [addNotification, pushToHistory]);

  const handleIsolateTree = useCallback((nodeId: string) => {
    const nodeToIsolate = companyNodes.find(n => n.id === nodeId);
    if (!nodeToIsolate) return;

    // Get all descendants of the selected node
    const getDescendants = (parentId: string): string[] => {
      const children = companyNodes.filter(n => 
        n.parentId === parentId || 
        (n.parentIds && n.parentIds.includes(parentId))
      );
      const descendants = children.map(c => c.id);
      children.forEach(child => {
        descendants.push(...getDescendants(child.id));
      });
      return descendants;
    };

    // Get all ancestors of the selected node (including external shareholders)
    const getAncestors = (childId: string): string[] => {
      const ancestors: string[] = [];
      const child = companyNodes.find(n => n.id === childId);
      if (child?.parentIds) {
        child.parentIds.forEach(parentId => {
          ancestors.push(parentId);
          ancestors.push(...getAncestors(parentId));
        });
      } else if (child?.parentId) {
        ancestors.push(child.parentId);
        ancestors.push(...getAncestors(child.parentId));
      }
      return ancestors;
    };

    // Get all nodes that should be visible in the isolated tree
    const nodesToShow = new Set([
      nodeId, // The selected node
      ...getDescendants(nodeId), // All children and their descendants
      ...getAncestors(nodeId) // All parents and their ancestors (including connected external shareholders)
    ]);

    // Hide all nodes not in the isolated tree (including unconnected external shareholders)
    pushToHistory();
    setCompanyNodes(prev => prev.map(node => ({
      ...node,
      isHidden: !nodesToShow.has(node.id)
    })));

    const visibleCount = nodesToShow.size;
    addNotification('success', 'Boom geïsoleerd', `Toont alleen ${nodeToIsolate.name} en ${visibleCount - 1} gerelateerde entiteiten`);
  }, [companyNodes, addNotification, pushToHistory]);

  // Handle logo click for easter egg
  const handleLogoClick = useCallback(() => {
    const newCount = logoClickCount + 1;
    setLogoClickCount(newCount);
    
    if (newCount === 20) {
      setShowSampleButton(true);
      addNotification('success', 'Demo ontgrendeld!', 'Voorbeeldgegevensknop is nu beschikbaar');
    }
  }, [logoClickCount, addNotification]);

  // Auto-save handler
  const handleAutoSave = useCallback(() => {
    if (companyNodes.length === 0 || !chartRef.current) return;
    
    try {
      // Get current node positions from ReactFlow
      const positions = chartRef.current.getNodePositions();
      
      const project: SavedProject = {
        version: '1.0.0',
        savedAt: new Date().toISOString(),
        companyNodes,
        nodePositions: positions,
        layout,
        maxDepth,
        showCountryFlags,
        percentageDisplayMode,
      };
      
      saveToLocalStorage(project);
    } catch (error) {
      console.warn('Auto-save failed:', error);
    }
  }, [companyNodes, layout, maxDepth, showCountryFlags, percentageDisplayMode]);

  // Auto-save effect - debounced
  useEffect(() => {
    if (companyNodes.length > 0 && autoSaveEnabled && chartRef.current) {
      // Debounce auto-save
      const timeoutId = setTimeout(() => {
        handleAutoSave();
      }, 2000); // Auto-save 2 seconds after changes
      
      return () => clearTimeout(timeoutId);
    }
  }, [companyNodes, layout, maxDepth, showCountryFlags, autoSaveEnabled, handleAutoSave]);

  // Save project to file
  const handleSaveProject = useCallback(async () => {
    if (companyNodes.length === 0) {
      addNotification('warning', 'Geen gegevens', 'Geen gegevens om op te slaan');
      return;
    }
    
    try {
      const positions = chartRef.current?.getNodePositions() || {};
      
      const project: SavedProject = {
        version: '1.0.0',
        savedAt: new Date().toISOString(),
        companyNodes,
        nodePositions: positions,
        layout,
        maxDepth,
        showCountryFlags,
        percentageDisplayMode,
      };
      
      saveProjectToFile(project);
      addNotification('success', 'Project opgeslagen', 'Project succesvol opgeslagen naar bestand');
    } catch (error) {
      addNotification('error', 'Opslaan mislukt', `Kon project niet opslaan: ${error}`);
    }
  }, [companyNodes, layout, maxDepth, showCountryFlags, percentageDisplayMode, addNotification]);

  // Load project from file
  const handleLoadProject = useCallback(async (file: File) => {
    try {
      setLoading(true);
      const project = await loadProjectFromFile(file);
      
      // Restore all state
      setCompanyNodes(project.companyNodes);
      setMaxDepth(project.maxDepth);
      setSettings(prev => ({
        ...prev,
        layoutDirection: project.layout,
        showCountryFlags: project.showCountryFlags,
        percentageDisplayMode: project.percentageDisplayMode ?? 'card',
      }));
      setSelectedNodeId(null);
      
      // Restore node positions and initialize history after a brief delay
      setTimeout(() => {
        if (chartRef.current && project.nodePositions) {
          chartRef.current.setNodePositions(project.nodePositions);
        }
      const snapshot: HistoryState = {
        companyNodes: project.companyNodes,
        nodePositions: project.nodePositions || {},
        layout: project.layout,
        maxDepth: project.maxDepth,
        showCountryFlags: project.showCountryFlags,
        percentageDisplayMode: project.percentageDisplayMode,
      };
      historyManagerRef.current.initialize(snapshot);
        setCanUndo(false);
        setCanRedo(false);
        handleFitView();
      }, 500);
      
      addNotification('success', 'Project geladen', `Project met ${project.companyNodes.length} entiteiten succesvol geladen`);
    } catch (error) {
      addNotification('error', 'Laden mislukt', `Kon project niet laden: ${error}`);
    } finally {
      setLoading(false);
    }
  }, [addNotification, handleFitView]);

  // Handle load project button click
  const handleLoadProjectClick = useCallback(() => {
    projectLoadInputRef.current?.click();
  }, []);

  // Handle file input change
  const handleLoadProjectFile = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleLoadProject(file);
    }
    // Reset input so same file can be selected again
    if (projectLoadInputRef.current) {
      projectLoadInputRef.current.value = '';
    }
  }, [handleLoadProject]);

  // Undo handler
  const handleUndo = useCallback(() => {
    const previousState = historyManagerRef.current.undo();
    if (previousState) {
      restoreFromHistory(previousState);
      setCanUndo(historyManagerRef.current.canUndo());
      setCanRedo(historyManagerRef.current.canRedo());
      addNotification('success', 'Ongedaan gemaakt', 'Vorige actie ongedaan gemaakt');
    }
  }, [restoreFromHistory, addNotification]);

  // Redo handler
  const handleRedo = useCallback(() => {
    const nextState = historyManagerRef.current.redo();
    if (nextState) {
      restoreFromHistory(nextState);
      setCanUndo(historyManagerRef.current.canUndo());
      setCanRedo(historyManagerRef.current.canRedo());
      addNotification('success', 'Opnieuw uitgevoerd', 'Actie opnieuw uitgevoerd');
    }
  }, [restoreFromHistory, addNotification]);

  // Handle node drag end - add to history
  const handleNodesDragEnd = useCallback(() => {
    // Debounce to avoid too many history entries during rapid dragging
    pushToHistory();
  }, [pushToHistory]);

  // Track layout changes
  const handleLayoutChange = useCallback((newLayout: LayoutDirection) => {
    if (layout !== newLayout) {
      pushToHistory();
      setSettings(prev => ({ ...prev, layoutDirection: newLayout }));
    }
  }, [layout, pushToHistory]);

  // Track depth changes
  const handleDepthChange = useCallback((newDepth: number | 'all') => {
    if (maxDepth !== newDepth) {
      pushToHistory();
      setMaxDepth(newDepth);
    }
  }, [maxDepth, pushToHistory]);

  // Track country flags toggle
  const handleToggleCountryDisplay = useCallback(() => {
    pushToHistory();
    setSettings(prev => ({ ...prev, showCountryFlags: !prev.showCountryFlags }));
  }, [pushToHistory]);

  // Apply pending edits from EntityTable
  const handleTableApply = useCallback((newNodes: CompanyNode[], hasStructural: boolean) => {
    pushToHistory();
    setCompanyNodes(newNodes);
    if (hasStructural && chartRef.current) {
      chartRef.current.resetLayout();
    }
    addNotification('success', 'Wijzigingen toegepast', `${newNodes.length} entiteiten bijgewerkt`);
  }, [pushToHistory, addNotification]);

  // Export to Excel
  const handleExportExcel = useCallback(async () => {
    if (companyNodes.length === 0) {
      addNotification('warning', 'Geen data', 'Geen entiteiten om te exporteren');
      return;
    }
    try {
      await downloadExcel(companyNodes);
      addNotification('success', 'Excel geëxporteerd', `${companyNodes.length} entiteiten`);
    } catch (err) {
      addNotification('error', 'Export mislukt', String(err));
    }
  }, [companyNodes, addNotification]);

  // Handle percentage display mode toggle
  const handleTogglePercentageDisplay = useCallback(() => {
    pushToHistory();
    setSettings(prev => ({
      ...prev,
      percentageDisplayMode:
        prev.percentageDisplayMode === 'card' ? 'line' :
        prev.percentageDisplayMode === 'line' ? 'both' :
        'card',
    }));
  }, [pushToHistory]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip shortcuts when user is typing in an input field
      const target = event.target as HTMLElement;
      const tagName = target.tagName.toLowerCase();
      const isInputField = tagName === 'input' || tagName === 'textarea' || tagName === 'select' || target.isContentEditable;

      // Ctrl+Z or Cmd+Z for undo
      if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
        event.preventDefault();
        if (canUndo) {
          handleUndo();
        }
        return;
      }
      // Ctrl+Y or Ctrl+Shift+Z or Cmd+Shift+Z for redo
      if (
        ((event.ctrlKey || event.metaKey) && event.key === 'y') ||
        ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'Z')
      ) {
        event.preventDefault();
        if (canRedo) {
          handleRedo();
        }
        return;
      }

      // Ctrl+F / Cmd+F → focus search input
      if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        event.preventDefault();
        const searchInput = document.querySelector('.search-input') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
        return;
      }

      // Ctrl+A / Cmd+A → select all visible nodes
      if ((event.ctrlKey || event.metaKey) && event.key === 'a' && !isInputField) {
        event.preventDefault();
        const allVisibleIds = filteredNodes.map(n => n.id);
        setSelectedNodeIds(allVisibleIds);
        return;
      }

      // Don't process remaining shortcuts when in input fields
      if (isInputField) return;

      // Home → fit view
      if (event.key === 'Home') {
        event.preventDefault();
        handleFitView();
        return;
      }

      // Delete / Backspace → hide selected node(s)
      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        if (selectedNodeIds.length > 0) {
          selectedNodeIds.forEach(id => handleToggleHide(id));
        } else if (selectedNodeId) {
          handleToggleHide(selectedNodeId);
        }
        return;
      }

      // + or = → zoom in
      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        if (chartRef.current) {
          chartRef.current.zoomIn();
        }
        return;
      }

      // - → zoom out
      if (event.key === '-') {
        event.preventDefault();
        if (chartRef.current) {
          chartRef.current.zoomOut();
        }
        return;
      }

      // Escape → clear selection
      if (event.key === 'Escape') {
        event.preventDefault();
        setSelectedNodeId(null);
        setSelectedNodeIds([]);
        setSearchTerm('');
        setShowSuggestions(false);
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canUndo, canRedo, handleUndo, handleRedo, selectedNodeId, selectedNodeIds, filteredNodes, handleFitView, handleToggleHide]);

  // Load auto-saved project on mount (client-side memory)
  useEffect(() => {
    const project = loadFromLocalStorage();
    if (!project) return;

    setCompanyNodes(project.companyNodes);
    setMaxDepth(project.maxDepth);
    setSettings(prev => ({
      ...prev,
      layoutDirection: project.layout,
      showCountryFlags: project.showCountryFlags,
      percentageDisplayMode: project.percentageDisplayMode ?? 'card',
    }));
    setSelectedNodeId(null);

    setTimeout(() => {
      if (chartRef.current && project.nodePositions) {
        chartRef.current.setNodePositions(project.nodePositions);
      }
      const snapshot: HistoryState = {
        companyNodes: project.companyNodes,
        nodePositions: project.nodePositions || {},
        layout: project.layout,
        maxDepth: project.maxDepth,
        showCountryFlags: project.showCountryFlags,
        percentageDisplayMode: project.percentageDisplayMode,
      };
      historyManagerRef.current.initialize(snapshot);
      setCanUndo(false);
      setCanRedo(false);
      handleFitView();
    }, 500);
  }, [handleFitView]);

  return (
    <div className="App">
      {/* Navigation Bar */}
      <NavigationBar
        onFitView={handleFitView}
        onExpandAll={handleExpandAll}
        onCollapseAll={handleCollapseAll}
        onShowAll={handleShowAll}
        layout={layout}
        onLayoutChange={handleLayoutChange}
        maxDepth={maxDepth}
        onDepthChange={handleDepthChange}
        showCountryFlags={showCountryFlags}
        onToggleCountryDisplay={handleToggleCountryDisplay}
        percentageDisplayMode={percentageDisplayMode}
        onTogglePercentageDisplay={handleTogglePercentageDisplay}
        showEntityTable={showEntityTable}
        onToggleEntityTable={() => setShowEntityTable(p => !p)}
        showSettingsPanel={showSettingsPanel}
        onToggleSettingsPanel={() => setShowSettingsPanel(p => !p)}
        totalNodes={companyNodes.length}
        visibleNodes={filteredNodes.length}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        onSearchClear={handleSearchClear}
        searchSuggestions={searchResults}
        onSuggestionSelect={handleSuggestionSelect}
        showSuggestions={showSuggestions}
        selectedNodeIds={selectedNodeIds}
        onBulkHide={handleBulkHide}
        onBulkColor={handleBulkColor}
        onClearSelection={() => { setSelectedNodeIds([]); setSelectedNodeId(null); }}
        loading={loading}
        onFileLoaded={async (file) => {
          setLoading(true);
          try {
            const companyData = await ExcelProcessor.processFile(file);
            const validation = ExcelProcessor.validateCompanyData(companyData);
            handleValidationResult(validation);
            if (!validation.isValid) { setLoading(false); return; }
            const nodes = ExcelProcessor.processCompanyData(companyData);
            handleDataLoaded(nodes);
          } catch (error) {
            handleError((error as Error).message);
          } finally {
            setLoading(false);
          }
        }}
        onLoadProject={handleLoadProjectClick}
        onSaveProject={handleSaveProject}
        hasProject={companyNodes.length > 0}
        onUndo={handleUndo}
        onRedo={handleRedo}
        onReset={handleReset}
        canUndo={canUndo}
        canRedo={canRedo}
        onExportPNG={handleExportPNG}
        onExportSVG={handleExportSVG}
        onExportExcel={handleExportExcel}
        showSampleButton={showSampleButton}
        onLoadSampleData={async () => {
          setLoading(true);
          try {
            const companyData = SampleDataGenerator.generateLargeDataset();
            const validation = ExcelProcessor.validateCompanyData(companyData);
            handleValidationResult(validation);
            const nodes = ExcelProcessor.processCompanyData(companyData);
            handleDataLoaded(nodes);
          } catch (error) {
            handleError((error as Error).message);
          } finally {
            setLoading(false);
          }
        }}
        onLogoClick={handleLogoClick}
      />

      {/* Main Content */}
      <main className="main-content">
        {showSettingsPanel && (
          <SettingsPanel
            settings={settings}
            onChange={setSettings}
            onClose={() => setShowSettingsPanel(false)}
          />
        )}
        {/* Chart Area */}
        <div className="chart-area">
          {companyNodes.length > 0 ? (
            <OrganizationChart
              ref={chartRef}
              companyNodes={filteredNodes}
              selectedNodeId={selectedNodeId}
              selectedNodeIds={selectedNodeIds}
              onNodeClick={handleNodeClick}
              onToggleExpand={handleToggleExpand}
              onToggleHide={handleToggleHide}
              onCustomizeNode={handleCustomizeNode}
              onIsolateTree={handleIsolateTree}
              onSelectionChange={handleSelectionChange}
              onPaneClick={handlePaneClick}
              layout={layout}
              maxDepth={maxDepth}
              showCountryFlags={showCountryFlags}
              percentageDisplayMode={percentageDisplayMode}
              showClusterGrouping={showClusterGrouping}
              onNodesDragEnd={handleNodesDragEnd}
              showGrid={settings.showGrid}
              showSingleShareholder={settings.showSingleShareholder}
              showDirectors={settings.showDirectors}
            />
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#6b7280',
              fontSize: '1.125rem'
            }}>
              Laad een Excel bestand om de organisatie chart te genereren
            </div>
          )}
        </div>
        {showEntityTable && companyNodes.length > 0 && (
          <EntityTable
            companyNodes={companyNodes}
            onApply={handleTableApply}
            onClose={() => setShowEntityTable(false)}
          />
        )}
      </main>

      {/* Notification System */}
      <NotificationSystem
        notifications={notifications}
        onRemove={removeNotification}
      />

      {/* Hidden file input for loading projects */}
      <input
        ref={projectLoadInputRef}
        type="file"
        accept=".json"
        onChange={handleLoadProjectFile}
        style={{ display: 'none' }}
      />
    </div>
  );
}

export default App;